import { useEffect, useMemo, useState } from "react";

import { putUserState, updateUserInfo } from "@/apis/user";
import AlertModal from "@/components/Modal/AlertModal";
import ConfirmModal from "@/components/Modal/ConfirmModal";
import UserModal from "@/components/Modal/UserModal";
import Table, { type SortState, type SortDirection } from "@/components/Table";
import {
  APPROVE_USER_MESSAGE,
  CONFIRM_APPROVE_USER_MESSAGE,
  CONFIRM_REJECT_USER_MESSAGE,
  EMPTY_USER_LIST_MESSAGE,
  REJECT_USER_MESSAGE,
} from "@/constants/messages/admin";
import { REQUEST_ERROR_MESSAGE } from "@/constants/messages/common";
import useModal from "@/hooks/useModal";
import UserDetails from "@/pages/Admin/components/UserInfo/UserDetails";
import { CellInfo } from "@/types/table";
import { UserByState } from "@/types/user";
import { getTableBody, getTableHeader, getUserBodyData } from "@/utils/table";

const userTableHeader = [
  "이름",
  "공동체",
  // "다락방",
  // "순",
  // "연락처", // 상세에서 볼 수 있으므로 주석 처리
  "권한",
  "가입 여부",
  "가입일자",
  "", // 상세 (본문 버튼 렌더 위해 비워둠)
  "", // 삭제
  "", // 권한 변경
];
// 헤더 표시용 라벨 (본문 액션셀은 name이 비어야 버튼이 렌더되므로 분리)
const userTableHeaderLabels = [
  "이름",
  "공동체",
  "권한",
  "가입 여부",
  "가입일자",
  "관리", // 액션 컬럼 (행 상태에 따라 상세/승인 등)
  "",
  "",
];
// 컬럼 폭 고정 (페이지 변경 시 폭이 변하지 않도록)
const userTableWidths = [
  "15%", // 이름
  "17%", // 공동체
  "10%", // 권한
  "12%", // 가입 여부
  "18%", // 가입일자
  "9%", // 상세
  "9%", // 삭제
  "10%", // 권한 변경
];
const header = getTableHeader(userTableHeaderLabels, userTableWidths);

interface UserTable {
  users: UserByState[];
  updateUsers: () => void;
}
const SORTABLE_COLUMNS = ["이름", "공동체", "가입일자"];

const UserTable = ({ users, updateUsers }: UserTable) => {
  const [userBody, setUserBody] = useState<CellInfo[][]>([]);
  const [selectedUser, setSelectedUser] = useState<UserByState | null>(null);
  const [sortState, setSortState] = useState<SortState>({ column: "이름", direction: "asc" });

  const [alertMessage, setAlertMessage] = useState("");
  const alertModal = useModal();
  const userModal = useModal();
  const confirmApproveModal = useModal();
  const confirmRejectModal = useModal();
  const confirmUpdateLeaderModal = useModal();

  const changeUserState = async (userStateCode: string, userId: string) => {
    await putUserState({ userStateCode, userId });
    updateUsers();
  };

  // 2025.05.27 권한변경 추가
  const [selectedRole, setSelectedRole] = useState("LEADER");

  const roleOptions = [
    { roleId: "ADMIN", roleName: "시스템 관리자" },
    { roleId: "LEADER", roleName: "리더십멤버" },
    { roleId: "MINISTRY", roleName: "목회 지원" },
    { roleId: "USER", roleName: "일반 사용자" },
  ];
  // 2025.05.27 권한변경 추가

  // 일반 유저의 권한을 리더로 업데이트하는 함수
  const updateLeader = async (tokenId: string, users: UserByState,role: string) => {
    await updateUserInfo(tokenId, {
      userName: users.userName,
      email: users.email || "123@kakao.com",
      telNo: users.telNo,
      cpsCd: "PTK",
      cmtCd: users.cmtCd,
      garCd: users.garCd,
      leafCd: users.leafCd,
      roleId: role,
    });
  };

  const [actionLoading, setActionLoading] = useState(false);

  const handleConfirmApprove = async () => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      if (!selectedUser) {
        setAlertMessage(REQUEST_ERROR_MESSAGE);
      } else {
        await changeUserState("01", selectedUser?.userId);
        setAlertMessage(APPROVE_USER_MESSAGE);
      }
    } catch {
      setAlertMessage(REQUEST_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
    alertModal.onOpen();
    confirmApproveModal.onClose();
  };

  const handleConfirmReject = async () => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      if (!selectedUser) {
        setAlertMessage(REQUEST_ERROR_MESSAGE);
      } else {
        await changeUserState("00", selectedUser?.userId);
        setAlertMessage(REJECT_USER_MESSAGE);
      }
    } catch {
      setAlertMessage(REQUEST_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
    alertModal.onOpen();
    confirmRejectModal.onClose();
  };

  const handleConfirmUpdateLeader = async (role: string) => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      if (!selectedUser) {
        setAlertMessage("요청을 처리할 수 없습니다. 다시 시도해 주세요.");
      } else {
        await updateLeader(selectedUser?.userId, selectedUser, role);
        setAlertMessage("해당 유저의 권한 변경이 승인되었습니다.");
        updateUsers();
      }
    } finally {
      setActionLoading(false);
    }
    alertModal.onOpen();
    confirmUpdateLeaderModal.onClose();
  };

  const sortedUsers = useMemo(() => {
    if (!sortState.direction) return users;
    const sorted = [...users].sort((a, b) => {
      let valA = "";
      let valB = "";
      switch (sortState.column) {
        case "이름": valA = a.userName || ""; valB = b.userName || ""; break;
        case "공동체": valA = a.cmtNm || ""; valB = b.cmtNm || ""; break;
        case "가입일자": valA = a.createdDate || ""; valB = b.createdDate || ""; break;
        default: return 0;
      }
      return valA.localeCompare(valB, "ko");
    });
    return sortState.direction === "desc" ? sorted.reverse() : sorted;
  }, [users, sortState]);

  const handleSort = (column: string, direction: SortDirection) => {
    setSortState({ column, direction });
  };

  useEffect(() => {
    const reservationTableBody = sortedUsers.map(user => {
      const showUserModal = () => {
        setSelectedUser(user);
        userModal.onOpen();
      };

      const approveUser = () => {
        setSelectedUser(user);
        confirmApproveModal.onOpen();
      };

      const rejectUser = () => {
        setSelectedUser(user);
        confirmRejectModal.onOpen();
      };

      // 일반 유저를 리더 권한으로 업데이트하는 함수
      const updateLeaderMethod = () => {
        setSelectedUser(user);
        confirmUpdateLeaderModal.onOpen();
      };

      return getUserBodyData(
        user,
        showUserModal,
        approveUser,
        rejectUser,
        updateLeaderMethod,
      );
    });

    setUserBody(getTableBody(userTableHeader, reservationTableBody));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedUsers]);

  if (users.length === 0 && !alertModal.isOpen) {
    return (
      <div className="flex h-20 items-center justify-center">
        <div>{EMPTY_USER_LIST_MESSAGE}</div>
      </div>
    );
  }

  return (
    <>
      <Table
        header={header}
        body={userBody}
        sortableColumns={SORTABLE_COLUMNS}
        sortState={sortState}
        onSort={handleSort}
      />
      {userModal.isOpen && (
        <UserModal onClose={userModal.onClose}>
          <UserModal.Header>회원 상세 정보</UserModal.Header>
          <UserModal.Content>
            {selectedUser && <UserDetails user={selectedUser} onUpdated={() => { userModal.onClose(); updateUsers(); }} />}
          </UserModal.Content>
          <UserModal.Footer>
            <button
              type="button"
              onClick={userModal.onClose}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
            >
              확인
            </button>
          </UserModal.Footer>
        </UserModal>
      )}

      {confirmApproveModal.isOpen && (
        <ConfirmModal
          title="회원 가입 승인"
          onConfirm={handleConfirmApprove}
          onClose={confirmApproveModal.onClose}
        >
          {CONFIRM_APPROVE_USER_MESSAGE}
        </ConfirmModal>
      )}
      {confirmRejectModal.isOpen && (
        <ConfirmModal
          title="회원 가입 반려"
          onConfirm={handleConfirmReject}
          onClose={confirmRejectModal.onClose}
        >
          {CONFIRM_REJECT_USER_MESSAGE}
        </ConfirmModal>
      )}
      {confirmUpdateLeaderModal.isOpen && (
      <ConfirmModal
        title="권한 부여"
        onConfirm={() => handleConfirmUpdateLeader(selectedRole)}
        onClose={confirmUpdateLeaderModal.onClose}
      >
        <div className="space-y-2">
          <p>권한을 선택하고 확인 버튼을 클릭하세요.</p>

          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="w-full border rounded px-2 py-1"
          >
            {roleOptions.map((role) => (
              <option key={role.roleId} value={role.roleId}>
                {role.roleName}
              </option>
            ))}
          </select>
        </div>
      </ConfirmModal>
      )}
      {alertModal.isOpen && (
        <AlertModal onClose={alertModal.onClose}>{alertMessage}</AlertModal>
      )}
    </>
  );
};

export default UserTable;
